import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useDeleteConfirmation } from '@client/hooks/ui/useDeleteConfirmation';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { type Key, useEffect, useState } from 'react';
import { type TrashRow, useTrashTableColumns } from './columns';
import { TrashFilters } from './TrashFilters';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/trash')({
    staticData: {
        metadata: { title: 'Trash' },
        ui: {
            icon: Trash2,
            title: 'Trash',
            subtitle: 'Review, restore or permanently delete files removed from your libraries',
            menuDescription: 'Restore or permanently delete deleted library files',
        },
        permissions: ['org:admin'],
    },
    component: OrgTrash,
});

type QueryVariables = RouterInputs['file']['listDeletedFiles'];
type Where = QueryVariables['where'];

function OrgTrash() {
    const [queryVariables, setQueryVariables] = usePathState<QueryVariables>('queryVariables', {
        orderBy: { deletedAt: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
    } as QueryVariables);

    const [filterWhere, setFilterWhere] = useState<Where>({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    // Ids hidden optimistically while a restore / permanent-delete is in flight,
    // so the row disappears immediately instead of lingering until the request
    // (which includes the storage delete) and refetch complete.
    const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(() => new Set());

    const { data, isFetching, refetch } = trpc.file.listDeletedFiles.useQuery({
        ...queryVariables,
        where: filterWhere,
    });

    // Clear optimistic removals once a fresh server result arrives (the affected
    // rows are already absent from it on success).
    useEffect(() => {
        setRemovedIds(new Set());
    }, [data]);

    const hideRows = (ids: string[]) => setRemovedIds((prev) => new Set([...prev, ...ids]));
    const unhideRows = (ids: string[]) =>
        setRemovedIds((prev) => {
            const next = new Set(prev);
            for (const id of ids) next.delete(id);
            return next;
        });

    const rows = (data?.rows ?? []).filter((row) => !removedIds.has(row.id));
    const total = data?.total ?? 0;

    const restoreMutation = trpc.file.restoreFiles.useMutation();
    const permanentlyDeleteMutation = trpc.file.permanentlyDeleteFile.useMutation();
    const emptyTrashMutation = trpc.file.emptyTrash.useMutation();

    const handleRestore = (ids: string[]) => {
        hideRows(ids);
        restoreMutation.mutate(
            { ids },
            {
                onSuccess: () => {
                    message.success(ids.length > 1 ? `${ids.length} files restored` : 'File restored');
                    setSelectedRowKeys((prev) => prev.filter((key) => !ids.includes(key as string)));
                    void refetch();
                },
                onError: (error) => {
                    unhideRows(ids);
                    errorMessage.show(error);
                },
            },
        );
    };

    // Single type-to-confirm modal drives permanent-delete of a single row, the
    // bulk selection, and empty-trash. `ids === null` means empty trash.
    const { showConfirmationModal, contextHolder } = useDeleteConfirmation<{ ids: string[] | null }>({
        title: 'Permanently delete',
        content:
            'This permanently removes the file(s) and their stored data. This action cannot be undone. Type the confirmation text below to proceed.',
        okText: 'Permanently delete',
        entityDisplayName: 'file',
        onConfirm: async ({ params }) => {
            const idsToHide = params.ids ?? rows.map((row) => row.id);
            hideRows(idsToHide);
            try {
                const result =
                    params.ids === null
                        ? await emptyTrashMutation.mutateAsync({})
                        : await permanentlyDeleteMutation.mutateAsync({ ids: params.ids });
                message.success(`${result.deleted} file${result.deleted === 1 ? '' : 's'} permanently deleted`);
                setSelectedRowKeys([]);
                void refetch();
            } catch (error) {
                unhideRows(idsToHide);
                errorMessage.show(error);
            }
        },
    });

    const promptPermanentDeleteRow = (row: TrashRow) => showConfirmationModal(row.name, { ids: [row.id] });
    const promptPermanentDeleteSelected = () => showConfirmationModal('DELETE', { ids: selectedRowKeys as string[] });
    const promptEmptyTrash = () => showConfirmationModal('DELETE', { ids: null });

    const columns = useTrashTableColumns({
        onRestore: (id) => handleRestore([id]),
        onPermanentDelete: promptPermanentDeleteRow,
    });

    const tableProps = usePrismaTable({
        data: rows,
        total,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: { position: 'both', showSizeChanger: true },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange(keys) {
                setSelectedRowKeys(keys);
            },
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
    });

    return (
        <PageController route={Route}>
            {contextHolder}
            <TableToolbar
                variant="enhanced"
                filters={
                    <TrashFilters
                        onChange={(where) => {
                            setFilterWhere(where);
                            setQueryVariables((prev) => ({ ...prev, offset: 0 }));
                        }}
                    />
                }
                actions={
                    <Space size="small">
                        {selectedRowKeys.length > 0 && (
                            <>
                                <span className="whitespace-nowrap text-muted-foreground text-sm">
                                    {selectedRowKeys.length} selected
                                </span>
                                <Button
                                    size="small"
                                    loading={restoreMutation.isPending}
                                    onClick={() => handleRestore(selectedRowKeys as string[])}
                                >
                                    Restore
                                </Button>
                                <Button
                                    type="danger"
                                    size="small"
                                    icon={<Trash2 size={14} />}
                                    onClick={promptPermanentDeleteSelected}
                                >
                                    Permanently delete
                                </Button>
                            </>
                        )}
                        <Button
                            type="danger"
                            size="small"
                            icon={<Trash2 size={14} />}
                            disabled={total === 0}
                            onClick={promptEmptyTrash}
                        >
                            Empty trash
                        </Button>
                    </Space>
                }
            />

            <PrismaTable {...tableProps} />
        </PageController>
    );
}
